import {
  type FruitShopV2Action,
  type FruitShopV2State,
  type FruitShopV2ViewerState,
  filterStateForViewer,
  reduceGameState,
  validateAction,
} from "@cloudnest/fruit-shop-v2-engine";
import {
  type FruitShopReplayEvent,
  toFruitShopReplayEvent,
} from "./event-adapter";
import type { FruitShopReplayFixtureEvent } from "./replay-fixtures.generated";

export interface FruitShopReplaySnapshot {
  readonly index: number;
  readonly event: FruitShopReplayFixtureEvent;
  readonly replayEvent: FruitShopReplayEvent;
  readonly state: FruitShopV2State;
  readonly viewerState: FruitShopV2ViewerState;
}

export interface BuildFruitShopReplayOptions {
  readonly observerUserId?: string;
}

function requireState(
  state: FruitShopV2State | null,
  eventType: FruitShopReplayEvent["eventType"],
): FruitShopV2State {
  if (state === null) {
    throw new Error(`${eventType} before init`);
  }
  return state;
}

function toAction(event: FruitShopReplayEvent): FruitShopV2Action | null {
  switch (event.eventType) {
    case "init":
    case "SyncViewerState":
      return null;
    case "ReadyForPriceSubmission":
      return { type: "ReadyForPriceSubmission", playerId: event.userId };
    case "SubmitPrices":
      return {
        type: "SubmitPrices",
        playerId: event.userId,
        prices: event.payload.prices,
      };
    case "UseSecret":
      return {
        type: "UseSecret",
        playerId: event.userId,
        productId: event.payload.productId,
      };
    case "UseChange":
      return {
        type: "UseChange",
        playerId: event.userId,
        fromProductId: event.payload.fromProductId,
        toProductId: event.payload.toProductId,
      };
    case "RevealRound":
      return { type: "RevealRound", playerId: event.userId };
    case "ConfirmRoundResult":
      return { type: "ConfirmRoundResult", playerId: event.userId };
    case "StartExtensionRound":
      return { type: "StartExtensionRound", playerId: event.userId };
  }
}

function allPlayersReady(state: FruitShopV2State): boolean {
  return state.players.every((player) =>
    state.currentRound.itemReadyPlayerIds.includes(player.playerId),
  );
}

function allPlayersSubmittedPrices(state: FruitShopV2State): boolean {
  return state.players.every((player) =>
    Boolean(state.currentRound.priceSubmissions[player.playerId]),
  );
}

function autoAdvanceAfterAction(
  state: FruitShopV2State,
  action: FruitShopV2Action,
): FruitShopV2State {
  if (
    (action.type === "ReadyForPriceSubmission" ||
      action.type === "UseSecret" ||
      action.type === "UseChange") &&
    state.currentRound.phase === "ITEM_WINDOW" &&
    allPlayersReady(state)
  ) {
    return {
      ...state,
      currentRound: {
        ...state.currentRound,
        phase: "PRICE_SUBMISSION",
      },
    };
  }

  if (
    action.type === "SubmitPrices" &&
    state.currentRound.phase === "PRICE_SUBMISSION" &&
    allPlayersSubmittedPrices(state)
  ) {
    const revealAction: FruitShopV2Action = {
      type: "RevealRound",
      playerId: state.hostPlayerId,
    };
    if (validateAction(state, revealAction).valid) {
      return reduceGameState(state, revealAction);
    }
  }

  return state;
}

function applyReplayEvent(
  state: FruitShopV2State | null,
  event: FruitShopReplayEvent,
): FruitShopV2State {
  if (event.eventType === "init") {
    return event.payload.state;
  }

  const current = requireState(state, event.eventType);
  if (event.eventType === "SyncViewerState") {
    return current;
  }

  const action = toAction(event);
  if (action === null) {
    return current;
  }

  const validation = validateAction(current, action);
  if (!validation.valid) {
    throw new Error(
      `${event.eventType} rejected: ${validation.error ?? "Validation failed"}`,
    );
  }

  return autoAdvanceAfterAction(reduceGameState(current, action), action);
}

export function buildFruitShopReplaySnapshots(
  events: readonly FruitShopReplayFixtureEvent[],
  options: BuildFruitShopReplayOptions = {},
): readonly FruitShopReplaySnapshot[] {
  const observerUserId = options.observerUserId ?? "observer";
  const snapshots: FruitShopReplaySnapshot[] = [];
  let state: FruitShopV2State | null = null;

  for (const event of events) {
    const replayEvent = toFruitShopReplayEvent(event);
    state = applyReplayEvent(state, replayEvent);
    snapshots.push({
      index: snapshots.length,
      event,
      replayEvent,
      state,
      viewerState: filterStateForViewer(state, observerUserId),
    });
  }

  if (state === null) {
    throw new Error("Cannot replay an empty Fruit Shop event log");
  }

  return snapshots;
}
